import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from './ui/sonner';
import { api, errorMessage } from '../lib/api';

const ContactFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    businessName: '',
    ownerName: '',
    email: '',
    phone: '',
    businessType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post('/forms/contact', formData);

      setIsSuccess(true);
      setIsSubmitting(false);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          businessName: '',
          ownerName: '',
          email: '',
          phone: '',
          businessType: '',
          message: ''
        });
        onClose();
      }, 3000);
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to submit form. Please try again.'));
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div 
              className="sticky top-0 p-6 border-b flex items-center justify-between"
              style={{ 
                background: 'linear-gradient(135deg, #6F5BEB 0%, #FF7A1A 100%)',
                borderTopLeftRadius: '1.5rem',
                borderTopRightRadius: '1.5rem'
              }}
            >
              <div>
                <h2 
                  className="text-3xl font-bold text-white"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  Talk to Sales
                </h2>
                <p 
                  className="text-white/90 mt-1"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  We'll get back to you within 24 hours
                </p>
              </div>
              <button
                onClick={onClose}
                type="button"
                aria-label="Close contact form"
                className="w-11 h-11 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              >
                <X size={24} className="text-white" aria-hidden="true" />
              </button>
            </div>

            {/* Form Content */}
            <div className="p-8">
              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div 
                    className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                    style={{ backgroundColor: '#10B981' }}
                  >
                    <Send size={40} className="text-white" />
                  </div>
                  <h3 
                    className="text-2xl font-bold mb-3"
                    style={{ fontFamily: 'Fraunces, serif', color: '#111827' }}
                  >
                    Thank you for your interest!
                  </h3>
                  <p 
                    className="text-lg"
                    style={{ fontFamily: 'Inter, sans-serif', color: '#64748b' }}
                  >
                    We'll be in touch within 24 hours with pricing and next steps.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="contact-business-name"
                        className="block text-sm font-semibold mb-2"
                        style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}
                      >
                        Business Name *
                      </label>
                      <Input
                        id="contact-business-name"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleChange}
                        required
                        placeholder="e.g., FitPoint Gym"
                        className="w-full h-11 md:h-9"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-owner-name"
                        className="block text-sm font-semibold mb-2"
                        style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}
                      >
                        Your Name *
                      </label>
                      <Input
                        id="contact-owner-name"
                        name="ownerName"
                        value={formData.ownerName}
                        onChange={handleChange}
                        required
                        placeholder="John Smith"
                        className="w-full h-11 md:h-9"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-sm font-semibold mb-2"
                        style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}
                      >
                        Email *
                      </label>
                      <Input
                        type="email"
                        id="contact-email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@fitpoint.com"
                        className="w-full h-11 md:h-9"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-phone"
                        className="block text-sm font-semibold mb-2"
                        style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}
                      >
                        Phone *
                      </label>
                      <Input
                        type="tel"
                        id="contact-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="(555) 123-4567"
                        className="w-full h-11 md:h-9"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="contact-business-type"
                      className="block text-sm font-semibold mb-2"
                      style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}
                    >
                      Business Type *
                    </label>
                    <select
                      id="contact-business-type"
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                      required
                      className="w-full h-11 md:h-9 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <option value="">Select your business type</option>
                      <option value="gym">Gym / Fitness Studio</option>
                      <option value="salon">Hair Salon / Barber</option>
                      <option value="spa">Spa / Wellness</option>
                      <option value="realestate">Real Estate</option>
                      <option value="pet">Pet Services</option>
                      <option value="retail">Boutique / Retail</option>
                      <option value="home">Home Services</option>
                      <option value="beauty">Beauty / Tattoo / Massage</option>
                      <option value="financial">Insurance / Financial</option>
                      <option value="entertainment">Entertainment / Attractions</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="contact-message"
                      className="block text-sm font-semibold mb-2"
                      style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}
                    >
                      Tell us about your business (optional)
                    </label>
                    <Textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What are you looking to achieve with Tappd?"
                      rows={4}
                      className="w-full"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-6 text-lg font-bold rounded-xl"
                    style={{
                      background: 'linear-gradient(135deg, #FF7A1A 0%, #F59E0B 100%)',
                      color: '#FFFFFF'
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>

                  <p 
                    className="text-sm text-center"
                    style={{ fontFamily: 'Inter, sans-serif', color: '#64748b' }}
                  >
                    By submitting, you agree to receive communication from Tappd
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ContactFormModal;
